"use client";

import { INPUT, TD } from "@/components/tool-kit/styles";
import type { PrismaChecklistStrings } from "@/components/tools/prisma-checklist";
import { t } from "@/lib/i18n/interpolate";
import {
  type ChecklistKind,
  type Entries,
  entryFor,
  type ItemEntry,
  itemsFor,
  rowStarts,
} from "@/lib/tool-kit/research-discovery/prisma-items";

type Strings = PrismaChecklistStrings;

export function itemText(
  strings: Strings,
  kind: ChecklistKind,
  id: string
): string {
  const table: Record<string, string> =
    kind === "full" ? strings.items : strings.abstractItems;
  return table[id] ?? "";
}

export function sectionName(strings: Strings, key: string): string {
  return (strings.sections as Record<string, string>)[key] ?? key;
}

export function topicName(strings: Strings, key: string): string {
  return (strings.topics as Record<string, string>)[key] ?? key;
}

/** The checklist as tab-separated text, ready to paste into a spreadsheet or Word table. */
export function checklistTsv(
  strings: Strings,
  kind: ChecklistKind,
  entries: Entries
): string {
  const head = [
    strings.columns.section,
    strings.columns.item,
    strings.columns.text,
    strings.columns.location,
    strings.columns.status,
  ].join("\t");
  const rows = itemsFor(kind).map((item) => {
    const entry = entryFor(entries, kind, item.id);
    return [
      `${sectionName(strings, item.section)} / ${topicName(strings, item.topic)}`,
      item.id,
      itemText(strings, kind, item.id),
      entry.location,
      entry.status === "" ? "" : strings.statuses[entry.status],
    ].join("\t");
  });
  return [head, ...rows].join("\n");
}

export function ItemRow({
  strings,
  kind,
  index,
  entry,
  onChange,
}: {
  strings: Strings;
  kind: ChecklistKind;
  index: number;
  entry: ItemEntry;
  onChange: (next: ItemEntry) => void;
}) {
  const items = itemsFor(kind);
  const item = items[index];
  const starts = rowStarts(items, index);
  const statusOptions = [
    { value: "" as const, label: strings.statusNone },
    { value: "reported" as const, label: strings.statuses.reported },
    { value: "partly" as const, label: strings.statuses.partly },
    { value: "na" as const, label: strings.statuses.na },
  ];
  return (
    <>
      {starts.section ? (
        <tr className="break-after-avoid bg-muted/50">
          <th
            className="px-3 py-2 text-left font-semibold text-foreground"
            colSpan={4}
            scope="colgroup"
          >
            {sectionName(strings, item.section)}
          </th>
        </tr>
      ) : null}
      <tr className="break-inside-avoid align-top">
        <td className={`${TD} w-36 text-muted-foreground text-xs`}>
          {starts.topic ? topicName(strings, item.topic) : ""}
        </td>
        <td className={`${TD} font-medium`}>{item.id}</td>
        <td className={`${TD} min-w-64`}>{itemText(strings, kind, item.id)}</td>
        <td className={`${TD} min-w-44 space-y-2`}>
          <input
            aria-label={t(strings.locationLabel, { item: item.id })}
            className={INPUT}
            onChange={(event) =>
              onChange({ ...entry, location: event.target.value })
            }
            placeholder={strings.locationPlaceholder}
            value={entry.location}
          />
          <select
            aria-label={t(strings.statusLabel, { item: item.id })}
            className={`${INPUT} print:border-none print:p-0`}
            onChange={(event) =>
              onChange({
                ...entry,
                status: event.target.value as ItemEntry["status"],
              })
            }
            value={entry.status}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </td>
      </tr>
    </>
  );
}
